import React, { useRef, useState } from 'react';
import * as api from '../api';
import CallTypeDropdown from "../components/CallTypeDropdown";
import AgentDropdown from "../components/AgentDropdown";

function inferDirectionFromFilename(name = "") {
  const s = String(name || "").toLowerCase();
  if (s.includes("cc-que")) return "in";
  if (/(?:^|[^a-z0-9])out(?:$|[^a-z0-9])/.test(s)) return "out";
  return "out";
}

function createEmptyForm(idRef) {
  const id = idRef.current++;
  return {
    id,
    file: null,
    fileType: '',
    agent: { id: '', name: '' },
  };
}

function ensureSingleEmptyRow(forms, idRef) {
  const nonBlank = forms.filter(
    (f) => f.file || f.fileType || (f.agent && f.agent.name)
  );
  const blanks = forms.filter(
    (f) => !f.file && !f.fileType && !(f.agent && f.agent.name)
  );

  let lastBlank = blanks[0];
  if (!lastBlank) {
    lastBlank = createEmptyForm(idRef);
  }

  return [...nonBlank, lastBlank];
}

function UploadRow({
  form,
  disabled,
  onFileChange,
  onTypeChange,
  onAgentChange,
}) {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const direction = inferDirectionFromFilename(form.file?.name || "");

  const openPicker = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleZoneClick = (e) => {
    if (disabled) return;
    if (e.target.closest('button, a, input, label, textarea, select')) return;
    openPicker();
  };

  const handleZoneKeyDown = (e) => {
    if (disabled) return;
    if (!(e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    openPicker();
  };

  const handleFileSelected = (file) => {
    if (!file) {
      onFileChange(form.id, null);
      return;
    }
    if (!/\.mp3$/i.test(file.name) && file.type !== 'audio/mpeg') {
      alert('Моля, качете MP3 файл.');
      return;
    }
    onFileChange(form.id, file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragOver(false);
    const files = [...e.dataTransfer.files];
    const mp3 = files.find(
      (f) => /\.mp3$/i.test(f.name) || f.type === 'audio/mpeg'
    );
    if (mp3) {
      handleFileSelected(mp3);
    } else {
      alert('Моля, качете MP3 файл.');
    }
  };

  const baseZoneClass =
    'mt-4 border-2 border-dashed rounded-2xl p-8 grid place-items-center text-center transition-colors cursor-pointer';
  const zoneClass =
    baseZoneClass +
    (isDragOver ? ' bg-slate-900/50 border-slate-500' : ' border-slate-700');

  return (
    <div
      className={zoneClass}
      onClick={handleZoneClick}
      onKeyDown={handleZoneKeyDown}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragOver(false);
      }}
      onDrop={handleDrop}
      role="button"
      tabIndex={0}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/mpeg,.mp3"
        className="hidden"
        disabled={disabled}
        onChange={(e) => handleFileSelected(e.target.files?.[0] || null)}
      />
      <div>
        <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center bg-slate-800/70 border border-slate-700">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
            <path d="M12 5v14" /><path d="M5 12l7-7 7 7" />
          </svg>
        </div>
        <p className="mt-3 text-slate-300 font-semibold">
          Провлачете .mp3 или кликнете тук
        </p>
        {form.file && (
          <div className="mt-3 space-y-2">
            <div className="text-sm text-slate-400">
              Избран:
              <div className="mt-1 font-semibold text-slate-200 break-all">
                {form.file.name}
              </div>
            </div>
            <div className="text-sm text-slate-400">
              Вид:
              <span
                className={[
                  'ml-2 inline-flex items-center px-2 py-0.5 rounded-lg border text-xs font-semibold',
                  direction === 'in'
                    ? 'bg-emerald-500/15 border-emerald-600/40 text-emerald-300'
                    : 'bg-indigo-500/15 border-indigo-600/40 text-indigo-300',
                ].join(' ')}
              >
                {direction === 'in'
                  ? 'Входящо обаждане'
                  : 'Изходящо обаждане'}
              </span>
            </div>
            <div className="inline-flex flex-col sm:flex-row sm:items-center gap-2">
              <CallTypeDropdown
                value={form.fileType}
                onChange={(v) => onTypeChange(form.id, v)}
              />
              <AgentDropdown
                value={form.agent}
                onChange={(v) => onAgentChange(form.id, v)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard({ onAfterUpload }) {
  const formIdRef = useRef(1);
  const jobIdRef = useRef(1);

  const [forms, setForms] = useState(() => [createEmptyForm(formIdRef)]);
  const [jobs, setJobs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const nonEmptyForms = forms.filter((f) => f.file);
  const canUploadAll =
    !isUploading &&
    nonEmptyForms.length > 0 &&
    nonEmptyForms.every((f) => f.agent && f.agent.name && f.fileType);

  const uploadBtnClass = canUploadAll
    ? 'btn btn-primary hover:scale-[1.02] transition-transform'
    : 'btn btn-ghost opacity-60 cursor-not-allowed';

  const updateForm = (id, patch) => {
    setForms((prev) => {
      const mapped = prev.map((f) =>
        f.id === id ? { ...f, ...patch } : f
      );
      return ensureSingleEmptyRow(mapped, formIdRef);
    });
  };

  const handleFileChange = (id, file) => {
    updateForm(id, { file });
  };

  const handleTypeChange = (id, fileType) => {
    updateForm(id, { fileType });
  };

  const handleAgentChange = (id, agent) => {
    updateForm(id, { agent });
  };

  const patchJob = (jobId, updater) => {
    setJobs((prev) =>
      prev.map((job) =>
        job.id === jobId
          ? {
            ...job,
            ...(typeof updater === 'function' ? updater(job) : updater),
          }
          : job
      )
    );
  };

  const doUploadAll = async () => {
    const toSend = forms.filter(
      (f) => f.file && f.agent && f.agent.name && f.fileType
    );
    if (!toSend.length) return;

    setIsUploading(true);

    const newJobs = toSend.map((f) => ({
      id: jobIdRef.current++,
      filename: f.file.name,
      agentName: f.agent?.name || '',
      fileType: f.fileType,
      direction: inferDirectionFromFilename(f.file.name),
      status: 'queued',
      text: 'В опашка…',
      pct: 0,
      error: '',
    }));

    setJobs((prev) => [...prev, ...newJobs]);

    const runSingleUploadJob = async (form, jobId) => {
      let t1, t2, t3;
      const clearPhaseTimers = () => {
        if (t1) clearTimeout(t1);
        if (t2) clearTimeout(t2);
        if (t3) clearTimeout(t3);
      };

      patchJob(jobId, {
        status: 'uploading',
        text: 'Качване и оценяване…',
        pct: 20,
      });

      t1 = setTimeout(
        () =>
          patchJob(jobId, (job) => ({
            pct: Math.max(job.pct || 0, 55),
          })),
        800
      );
      t2 = setTimeout(
        () =>
          patchJob(jobId, {
            text: 'Автоматична оценка…',
            pct: 80,
          }),
        2000
      );

      try {
        await api.upload(
          form.file,
          form.fileType,
          form.agent?.name || ''
        );

        patchJob(jobId, {
          text: 'Финализиране…',
          pct: 95,
        });

        patchJob(jobId, {
          status: 'done',
          text: 'Оценката е приключила.',
          pct: 100,
        });
      } catch (e) {
        console.error(e);
        patchJob(jobId, {
          status: 'error',
          text: 'Грешка при качване',
          pct: 100,
          error: e?.message || 'Грешка при качване',
        });
      } finally {
        clearPhaseTimers();
      }
    };

    for (let i = 0; i < toSend.length; i += 1) {
      const form = toSend[i];
      const jobId = newJobs[i].id;

      await runSingleUploadJob(form, jobId);
    }

    setIsUploading(false);
    if (onAfterUpload) {
      onAfterUpload();
    }

    setForms([createEmptyForm(formIdRef)]);
  };

  const renderJobs = () => {
    if (!jobs.length) {
      return (
        <div className="p-6 text-slate-400">
          Няма активни задачи.
        </div>
      );
    }

    const statusBadge = (status) => {
      if (status === 'done') {
        return 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-300';
      }
      if (status === 'error') {
        return 'bg-rose-500/15 border border-rose-500/40 text-rose-300';
      }
      return 'bg-slate-500/15 border border-slate-500/40 text-slate-200';
    };

    const statusText = (status) => {
      if (status === 'done') return 'Готово';
      if (status === 'error') return 'Грешка';
      if (status === 'uploading') return 'Обработва се…';
      return 'В опашка';
    };

    return (
      <div className="p-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {jobs.map((job) => (
          <div
            key={job.id}
            className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/60 p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-100 truncate">
                  {job.filename}
                </div>
                <div className="mt-1 text-xs text-slate-500 space-x-2">
                  {job.direction && (
                    <span>
                      Вид:{' '}
                      <span
                        className={[
                          'ml-1 inline-flex items-center px-2 py-0.5 rounded-lg border text-[11px] font-semibold',
                          job.direction === 'in'
                            ? 'bg-emerald-500/15 border-emerald-600/40 text-emerald-300'
                            : 'bg-indigo-500/15 border-indigo-600/40 text-indigo-300',
                        ].join(' ')}
                      >
                        {job.direction === 'in'
                          ? 'Входящо обаждане'
                          : 'Изходящо обаждане'}
                      </span>
                    </span>
                  )}
                  {job.agentName && (
                    <span>Агент: {job.agentName}</span>
                  )}
                </div>
              </div>
              <span
                className={[
                  'inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold',
                  statusBadge(job.status),
                ].join(' ')}
              >
                {statusText(job.status)}
              </span>
            </div>
            <div className="text-sm text-slate-400">{job.text}</div>
            <div className="mt-3 progress">
              <span
                className="transition-[width] duration-500 ease-out"
                style={{
                  width: `${Math.max(5, Math.min(100, job.pct || 0))
                    }%`,
                }}
              />
            </div>
            {job.status === 'done' && (
              <div className="text-xs text-emerald-400 mt-2">
                Оценката е приключила.
              </div>
            )}
            {job.status === 'error' && job.error && (
              <div className="text-xs text-rose-400 mt-2">
                {job.error}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  return (
    <section className="space-y-6">
      <div className="card rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Оценяване</h2>
          <div className="text-sm text-slate-400">
            Поддържано: .mp3
          </div>
        </div>

        {forms.length === 1 ? (
          <div className="mt-4 max-w-xl mx-auto">
            <UploadRow
              form={forms[0]}
              disabled={isUploading}
              onFileChange={handleFileChange}
              onTypeChange={handleTypeChange}
              onAgentChange={handleAgentChange}
            />
          </div>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {forms.map((form) => (
              <UploadRow
                key={form.id}
                form={form}
                disabled={isUploading}
                onFileChange={handleFileChange}
                onTypeChange={handleTypeChange}
                onAgentChange={handleAgentChange}
              />
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            className={uploadBtnClass}
            onClick={doUploadAll}
            disabled={!canUploadAll}
            aria-disabled={!canUploadAll}
            title={
              !nonEmptyForms.length
                ? 'Добавете поне един MP3 файл'
                : isUploading
                  ? 'Обработва се…'
                  : 'Качване на всички'
            }
          >
            {isUploading ? 'Обработване…' : 'Качи всички'}
          </button>
        </div>
      </div>
      <div className="card rounded-2xl p-0 overflow-hidden">
        <div className="px-6 pt-6">
          <h2 className="text-lg font-bold">Активни задачи</h2>
        </div>
        <div className="divide-y divide-slate-800/80" id="jobs">
          {renderJobs()}
        </div>
      </div>
    </section>
  );
}
